import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/backend/config";

type AnnouncementSettings = {
  enabled: boolean;
  text: string;
  type: "info" | "warning" | "success" | "danger";
};

type SiteSettingResponse = {
  setting: {
    key: string;
    value: AnnouncementSettings;
    updated_at: string;
  };
};

async function fetchAnnouncement(): Promise<AnnouncementSettings | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/site-settings/announcement`);
    if (!res.ok) return null;
    const data: SiteSettingResponse = await res.json();
    return data.setting.value;
  } catch {
    return null;
  }
}

// Site's brand green color
const SITE_GREEN = "#2ebd85";

const typeStyles: Record<string, string> = {
  info: "bg-blue-500",
  warning: "bg-yellow-500",
  success: "", // Will use inline style for brand green
  danger: "bg-red-500",
};

export const Banner = () => {
  const { data: announcement } = useQuery({
    queryKey: ["site-announcement"],
    queryFn: fetchAnnouncement,
    staleTime: 1_800_000, // Cache for 30 minutes
    // Poll every 30 minutes when enabled, every hour when disabled
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.enabled ? 1_800_000 : 3_600_000; // 30 min : 60 min
    },
    retry: 1,
  });

  // Don't render if disabled, no text, or failed to fetch
  if (!announcement?.enabled || !announcement?.text) {
    return null;
  }

  const bgClass = typeStyles[announcement.type] || typeStyles.info;
  const isSuccess = announcement.type === "success";

  return (
    <div 
      className={`${!isSuccess ? bgClass : ""} text-white py-1 px-4 text-center text-xs`}
      style={isSuccess ? { backgroundColor: SITE_GREEN } : undefined}
    >
      <span className="font-medium">{announcement.text}</span>
    </div>
  );
};
